import {
  CloudSyncIcon,
  NoticeIcon,
  OfflineIcon,
  SuccessIcon,
  SyncIcon,
  UploadIcon,
  WarningIcon,
} from '@/components/icons';
import { Button, Popover, Typography, theme } from 'antd';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useOffline } from '@/lib/offline/offline-context';

const { Text } = Typography;

/**
 * Whether the counter is talking to the server, and what is waiting if it is not.
 *
 * Always on screen, because the one thing worse than losing the connection is
 * not knowing you have. A pharmacist who can see "working offline, 4 bills
 * waiting" keeps billing with confidence; one who cannot starts wondering
 * whether the last three sales were real.
 */
export function ConnectionPill() {
  const { token } = theme.useToken();
  const { online, queued, failed, syncing, durable, storageReason, sync, snapshot } = useOffline();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  /*
   * Silent while everything is fine.
   *
   * A green "Online" badge sitting there all day tells a counter nothing it did
   * not assume, and it competed with the shop's own status pill beside it — two
   * dots and two words, one saying Online and the other Offline, about entirely
   * different things.
   *
   * So this appears only when there is something to say: the line is down, work
   * is queued, something was refused, or the browser cannot store anything. The
   * absence of it is the healthy state.
   */
  const degraded = !online || syncing || queued > 0 || failed > 0 || !durable;
  if (!degraded) return null;

  const tone =
    failed > 0 || !durable ? token.colorError : online ? token.colorInfo : token.colorWarning;
  const label = syncing
    ? 'Syncing…'
    : failed > 0
      ? `${failed} not sent`
      : !online
        ? 'Working offline'
        : `${queued} to send`;

  const panel = (
    <div className="w-72 text-sm">
      <div className="flex items-start gap-2">
        {online ? (
          <SuccessIcon style={{ color: token.colorSuccess, marginTop: 3 }} />
        ) : (
          <OfflineIcon style={{ color: token.colorWarning, marginTop: 3 }} />
        )}
        <div className="min-w-0">
          <div className="font-medium">
            {online ? 'Connected to the server' : 'Working offline'}
          </div>
          <Text type="secondary" className="block text-xs leading-snug">
            {online
              ? 'Bills and stock go straight through.'
              : 'Billing still works. Everything you take is saved on this device and sent the moment the line comes back.'}
          </Text>
        </div>
      </div>

      {queued > 0 || failed > 0 || syncing ? (
        <div
          className="mt-3 border-0 border-t border-solid pt-3"
          style={{ borderColor: token.colorBorderSecondary }}
        >
          {queued > 0 ? (
            <div className="flex items-center justify-between gap-3">
              <Text className="text-sm">
                {queued} bill{queued === 1 ? '' : 's'} waiting to send
              </Text>
              {online && !syncing ? (
                <Button size="small" icon={<CloudSyncIcon />} onClick={() => void sync()}>
                  Send now
                </Button>
              ) : null}
            </div>
          ) : null}

          {/*
           * A refusal the server will never change its mind about — a bill
           * number already used, stock genuinely gone, a prescription medicine
           * with no prescriber. It stays here rather than being retried into
           * oblivion, because a bill in a customer's hand that exists nowhere
           * else is the thing this whole feature is for.
           */}
          {failed > 0 ? (
            <div className="mt-2 flex items-start gap-2">
              <NoticeIcon style={{ color: token.colorError, marginTop: 3 }} />
              <div className="min-w-0">
                <Text type="danger" className="block text-sm">
                  {failed} could not be sent
                </Text>
                <Text type="secondary" className="block text-xs leading-snug">
                  The server refused them and retrying will not help. Nothing is lost — they are
                  still stored on this device — but someone has to look at why.
                </Text>
                <Button
                  size="small"
                  type="link"
                  className="mt-1 h-auto! p-0!"
                  onClick={() => {
                    setOpen(false);
                    navigate('/settings');
                  }}
                >
                  See them in Settings
                </Button>
              </div>
            </div>
          ) : null}
        </div>
      ) : null}

      {/*
       * If IndexedDB is unavailable — a private window, a locked-down profile —
       * there is nowhere durable to queue anything, and the counter must be
       * told before it discovers this with a bill half-written.
       */}
      {!durable ? (
        <div
          className="mt-3 flex items-start gap-2 border-0 border-t border-solid pt-3"
          style={{ borderColor: token.colorBorderSecondary }}
        >
          <WarningIcon style={{ color: token.colorError, marginTop: 3 }} />
          <div className="min-w-0">
            <Text type="danger" className="block text-xs leading-snug">
              {storageReason ??
                'This browser will not let the app store anything. Offline billing is not safe here.'}
            </Text>
            <Button
              size="small"
              type="link"
              className="mt-1 h-auto! p-0!"
              onClick={() => {
                setOpen(false);
                navigate('/settings');
              }}
            >
              Check this device
            </Button>
          </div>
        </div>
      ) : null}

      {snapshot ? (
        <Text type="secondary" className="mt-3 block text-[11px]">
          Shelf and bill numbers as of {new Date(snapshot.capturedAt).toLocaleTimeString()}
        </Text>
      ) : null}
    </div>
  );

  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
      trigger="click"
      placement="bottomRight"
      content={panel}
    >
      <Button className="topbar-chip" style={{ color: tone }} aria-label={`Connection: ${label}`}>
        <span className="flex items-center gap-1.5">
          {syncing ? (
            <SyncIcon spin />
          ) : online ? (
            <UploadIcon />
          ) : (
            <OfflineIcon />
          )}
          <span className="hidden text-sm sm:inline">{label}</span>
        </span>
      </Button>
    </Popover>
  );
}
